import { useContext, useEffect, useMemo, useState } from "react";
import {
  collection,
  collectionGroup,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { LanguageContext } from "../context/LanguageContext";
import LeftMenu from "./menus/LeftMenu";
import HeaderBar from "./headers/HeaderBar";
import { Box } from "@mui/material";
import MetricCard from "./MetricCard";
import { subDays } from "date-fns";
import { statusColors } from "../constants/statusColors";
import { useTheme } from "@mui/material/styles";

export default function DashboardDesktop() {
  const { translate } = useContext(LanguageContext);
  const { currentUser } = useAuth();

  const [role, setRole] = useState("Normal User");
  const [currentUserProfile, setCurrentUserProfile] = useState(null);

  const [, setTotalProjects] = useState(0);
  const [, setTotalTickets] = useState(0);
  const [, setOpenCount] = useState(0);
  const [, setPendingCount] = useState(0);
  const [, setSolvedCount] = useState(0);
  const [, setAvgFirstReply] = useState(0);

  const [, setOpenedChange] = useState(0);
  const [, setPendingChange] = useState(0);
  const [, setSolvedChange] = useState(0);
  const [, setAvgReplyChange] = useState(0);

  const [statusCounts, setStatusCounts] = useState({});

  const theme = useTheme();

  // Profil + darkMode live
  useEffect(() => {
    if (!currentUser) return;
    const profileRef = doc(db, "profilsUsers", currentUser.uid);
    const unsub = onSnapshot(profileRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setRole(data.role ?? "Normal User");
      setCurrentUserProfile({ id: snap.id, ...data });
      // if (data.darkMode !== undefined) setDarkMode(data.darkMode);
      if (data.darkMode !== undefined) {
        window.dispatchEvent(
          new CustomEvent("darkmode:changed", { detail: data.darkMode })
        );
      }
    });
    return () => unsub();
  }, [currentUser]);

  // Stats
  useEffect(() => {
    if (!currentUserProfile?.companyId) return;

    // 0) Total projets
    getDocs(
      query(
        collection(db, "projects"),
        where("companyId", "==", currentUserProfile.companyId)
      )
    ).then((snap) => setTotalProjects(snap.size));

    // 0b) Total tickets
    getDocs(
      query(
        collectionGroup(db, "tickets"),
        where("companyId", "==", currentUserProfile.companyId)
      )
    ).then((snap) => setTotalTickets(snap.size));

    // 1) Tous les tickets
    getDocs(
      query(
        collectionGroup(db, "tickets"),
        where("companyId", "==", currentUserProfile.companyId)
      )
    ).then((snap) => {
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // → COUNTS PAR STATUT
      const counts = {};
      all.forEach((t) => {
        counts[t.status] = (counts[t.status] || 0) + 1;
      });
      setStatusCounts(counts);

      // Périodes : 3 derniers jours vs 3 jours précédents
      const now = new Date();
      const currStart = subDays(now, 3);
      const prevStart = subDays(now, 6);
      const prevEnd = currStart;

      const isIn = (t, from, to) => {
        const d = t.createdAt.toDate();
        return d >= from && d < to;
      };
      const currPeriod = all.filter((t) => isIn(t, currStart, now));
      const prevPeriod = all.filter((t) => isIn(t, prevStart, prevEnd));

      // — Opened
      const totalOpen = all.filter((t) => t.status === "Open").length;
      setOpenCount(totalOpen);
      const newOpenCurr = currPeriod.filter((t) => t.status === "Open").length;
      const newOpenPrev = prevPeriod.filter((t) => t.status === "Open").length;
      setOpenedChange(
        newOpenPrev > 0
          ? Math.round(((newOpenCurr - newOpenPrev) / newOpenPrev) * 100)
          : 0
      );

      // — Pending (Working on It)
      const totalPending = all.filter(
        (t) => t.status === "Working on It"
      ).length;
      setPendingCount(totalPending);
      const newPendingCurr = currPeriod.filter(
        (t) => t.status === "Working on It"
      ).length;
      const newPendingPrev = prevPeriod.filter(
        (t) => t.status === "Working on It"
      ).length;
      setPendingChange(
        newPendingPrev > 0
          ? Math.round(
              ((newPendingCurr - newPendingPrev) / newPendingPrev) * 100
            )
          : 0
      );

      // — Solved (Closed)
      const totalSolved = all.filter((t) => t.status === "Closed").length;
      setSolvedCount(totalSolved);
      const newSolvedCurr = currPeriod.filter(
        (t) => t.status === "Closed"
      ).length;
      const newSolvedPrev = prevPeriod.filter(
        (t) => t.status === "Closed"
      ).length;
      setSolvedChange(
        newSolvedPrev > 0
          ? Math.round(((newSolvedCurr - newSolvedPrev) / newSolvedPrev) * 100)
          : 0
      );

      // — Avg First Reply (période courante vs période précédente)
      Promise.all(
        currPeriod.map((t) =>
          getDocs(
            query(
              collection(
                db,
                "projects",
                t.projectId,
                "tickets",
                t.id,
                "activity"
              ),
              orderBy("timestamp", "asc")
            )
          ).then((snap) => {
            const first = snap.docs.find((d) =>
              /Comment$/.test(d.data().action)
            );
            return first
              ? (first.data().timestamp.toDate() - t.createdAt.toDate()) / 60000
              : null;
          })
        )
      ).then((deltasCurr) => {
        const validCurr = deltasCurr.filter((d) => d != null);
        const avgCurr =
          validCurr.reduce((a, b) => a + b, 0) / validCurr.length || 0;

        Promise.all(
          prevPeriod.map((t) =>
            getDocs(
              query(
                collection(
                  db,
                  "projects",
                  t.projectId,
                  "tickets",
                  t.id,
                  "activity"
                ),
                orderBy("timestamp", "asc")
              )
            ).then((snap) => {
              const first = snap.docs.find((d) =>
                /Comment$/.test(d.data().action)
              );
              return first
                ? (first.data().timestamp.toDate() - t.createdAt.toDate()) /
                    60000
                : null;
            })
          )
        ).then((deltasPrev) => {
          const validPrev = deltasPrev.filter((d) => d != null);
          const avgPrev =
            validPrev.reduce((a, b) => a + b, 0) / validPrev.length || 0;

          setAvgFirstReply(Math.round(avgCurr));
          setAvgReplyChange(
            avgPrev > 0 ? Math.round(((avgCurr - avgPrev) / avgPrev) * 100) : 0
          );
        });
      });
    });
  }, [currentUserProfile]);

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        bgcolor: "background.default",
        color: "text.primary",
        minHeight: "100vh",
      }}
    >
      <LeftMenu />

      <Box sx={{ width: "90%", boxSizing: "border-box", ml: "5px" }}>
        <HeaderBar
          title={translate("SupportTickets")}
          subtitle={translate("PleaseSendTicket")}
          showProjectSelect={false}
          userName={currentUser?.displayName}
          userRole={role}
        />

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            px: 3,
            py: 2,
            pt: "30px",
            bgcolor: "background.default", // ⬅️ fond du bloc métriques
          }}
        >
          {Object.entries(statusColors).map(([status, color]) => (
            <Box key={status} sx={{ flex: "0 0 200px" }}>
              <MetricCard
                title={translate(status)}
                value={statusCounts[status] || 0}
                unit=""
                color={color}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
